import React, { useEffect, useMemo, useState } from 'react';
import { Button, HStack, IconInfo, Stack, Text } from '@adc/parallax-component-library';
import { useApiError, useConnection, useLibrePartner } from 'Hooks';
import { Footer, InfoModal, Main, RichText, ScrollContainer, Spinner, TitleActionBar, } from 'Components/utility';
import { getDocumentPathFromLanguage } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { store } from 'Utilities/store';
const ConfirmPartnerConnection = ({ data: { partnerConnection, code, yearOfBirth }, }) => {
    const [loading, setLoading] = useState(false);
    const [showModalInfo, setShowModalInfo] = useState(false);
    const { acceptOrRejectPartnerConnection } = useLibrePartner();
    const { showApiErrorModal } = useApiError();
    const { getConnections } = useConnection();
    useEffect(() => {
        if (!partnerConnection) {
            mediator.publish('router:navigate', '/connections');
        }
    }, [partnerConnection]);
    const scopesData = useMemo(() => [
        ...new Set(partnerConnection?.consent.scopes.reduce((acc, scope) => {
            const splittedScope = scope.split('.');
            if (splittedScope.length > 1) {
                acc.push(splittedScope[1]);
            }
            return acc;
        }, [])),
    ], [partnerConnection]);
    const scopesStr = useMemo(() => {
        if (!scopesData.length) {
            return '';
        }
        const scopeStrArr = scopesData.map((key) => i18n.t(`Global.microcopy.scopes.${key}`).toLowerCase());
        scopeStrArr[0] = scopeStrArr[0].split('')[0].toUpperCase() + scopeStrArr[0].slice(1);
        return scopeStrArr.join(', ');
    }, [scopesData]);
    const scopesModal = useMemo(() => {
        return (React.createElement(Stack, { gap: "$4" }, scopesData.map((scope) => {
            return (React.createElement(Stack, { key: scope },
                React.createElement(Text, { testID: `ConfirmConnection.scope.${scope}`, marginBottom: "$2", color: "$text.100", fontWeight: "$bodyBase.strong", fontSize: "$bodyBase.strong" }, `${i18n.t(`Connections.modals.dataSharingBottomSheet.${scope}Title`)}`),
                React.createElement(Stack, { marginLeft: "$3" },
                    React.createElement(RichText, { fontToken: "$bodySmall.default", testID: `ConfirmConnection.scope.${scope}.description`, text: i18n.t(`Connections.modals.dataSharingBottomSheet.${scope}`) }))));
        })));
    }, [scopesData]);
    const onLinkClick = (href) => {
        const { documentsUrl, documents } = store.getState().config;
        const { language } = store.getState().lang;
        const document = href.includes('privacyNoticeLink')
            ? documents?.privacypolicy
            : documents?.toupat;
        const noticePath = getDocumentPathFromLanguage(document, language);
        if (noticePath) {
            const noticeFullUrl = `${documentsUrl}${noticePath}`;
            window.open(noticeFullUrl, '_blank');
        }
    };
    const onAcceptOrDeclineConnection = async (action) => {
        if (partnerConnection) {
            setLoading(true);
            const { error } = await acceptOrRejectPartnerConnection(partnerConnection.partner.id, action, code, yearOfBirth);
            if (error) {
                showApiErrorModal();
                setLoading(false);
                return;
            }
            const connections = await getConnections();
            const connection = connections.partnerConnections?.find((pc) => pc.partner.id === partnerConnection.partner.id);
            mediator.publish('router:navigate', {
                url: '/partner-connection',
                data: { partnerConnection: connection },
            });
            setLoading(false);
        }
    };
    return (React.createElement(Main, null,
        React.createElement(TitleActionBar, { testID: "ConfirmConnection", title: i18n.t('ConfirmCommercialApiConnection.title') }),
        loading && React.createElement(Spinner, { "aria-label": "loading", testID: "ConfirmConnection.spinner" }),
        React.createElement(ScrollContainer, { marginTop: 19 },
            React.createElement(Text, { testID: "ConfirmConnection.secondaryText", fontSize: "$bodyBase.default", fontWeight: "$bodyBase.default", lineHeight: "$bodyBase.default", color: "$text.100", marginHorizontal: "$4" }, i18n.t('ConfirmCommercialApiConnection.content.confirmConnectedApiPartner.primaryText', {
                partnerName: partnerConnection?.partner.name,
            })),
            React.createElement(HStack, { justifyContent: "space-between", alignItems: "flex-start", margin: "$4", padding: "$4", backgroundColor: "$surface.container", border: "$1.5", shadow: "$1" },
                React.createElement(Text, { testID: "ConfirmConnection.scopes", color: "$text.100", fontWeight: "$bodyBase.medium", fontSize: "$bodyBase.medium" }, scopesStr),
                React.createElement(Stack, { onPress: () => setShowModalInfo(true), cursor: "pointer" },
                    React.createElement(IconInfo, { testID: "ConfirmConnection.info-icon", size: "S", color: "$interactive.core.enabled" })))),
        React.createElement(Footer, { buttonText: "", customProps: React.createElement(Stack, { marginHorizontal: "$3" },
                React.createElement(Stack, { marginBottom: "$2" },
                    React.createElement(RichText, { text: i18n.t('ConfirmCommercialApiConnection.content.confirmConnectedApiPartner.secondaryText', { partnerName: partnerConnection?.partner.name }), color: "$text.60", testID: "ConfirmConnection.secondaryText", onPressLink: onLinkClick, fontToken: "$labelBase.default" })),
                React.createElement(RichText, { text: i18n.t('ConfirmCommercialApiConnection.content.confirmConnectedApiPartner.bodyText', { partnerName: partnerConnection?.partner.name }), color: "$text.60", testID: "ConfirmConnection.bodyText", onPressLink: onLinkClick, fontToken: "$labelBase.default" }),
                React.createElement(HStack, { justifyContent: "space-between", marginTop: "$4" },
                    React.createElement(Button, { marginRight: "$2", onPress: () => onAcceptOrDeclineConnection('decline'), testID: "ConfirmConnection.common.decline", "aria-label": i18n.t('Global.microcopy.common.decline'), label: i18n.t('Global.microcopy.common.decline'), flexGrow: 1, variant: "secondary" }),
                    React.createElement(Button, { marginLeft: "$2", onPress: () => onAcceptOrDeclineConnection('accept'), testID: "ConfirmConnection.common.allow", "aria-label": i18n.t('Global.microcopy.common.allow'), label: i18n.t('Global.microcopy.common.allow'), flexGrow: 1 }))) }),
        React.createElement(InfoModal, { isOpen: showModalInfo, title: i18n.t('Connections.modals.dataSharingBottomSheet.title'), customBody: scopesModal, handleClose: () => setShowModalInfo(false) })));
};
export default ConfirmPartnerConnection;
